import { addZonedDays, startOfZonedWeek, zonedDateTime } from '@/lib/reporting/timezone';
import { addDays, startOfWeekMonday } from '@/utils/appointmentCalendarDates';

/** Clock and period formatting for the Aftaler calendar (24-hour in both languages). */

export type AppointmentRangeView = 'day' | 'week' | 'month' | 'agenda';

export function appointmentDateLocale(language: string): string {
  return language.startsWith('da') ? 'da-DK' : 'en-GB';
}

export function formatAppointmentClock(date: Date, _language: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(date);
}

export function formatAppointmentTimeRange(
  start: Date,
  end: Date,
  language: string,
  timeZone?: string,
): string {
  return `${formatAppointmentClock(start, language, timeZone)}–${formatAppointmentClock(end, language, timeZone)}`;
}

export function formatHourLabel(hour: number, language: string): string {
  const date = new Date(2026, 0, 1, hour, 0, 0);
  return formatAppointmentClock(date, language);
}

/**
 * Danish sentence case. Lowercases the whole string first, so a title-cased
 * locale result ("Tirsdag Den 20. Oktober 2026") cannot keep "Den" or the month.
 */
export function danishSentenceCaseDate(formatted: string, locale: string): string {
  const lower = formatted.toLocaleLowerCase(locale);
  return lower.charAt(0).toLocaleUpperCase(locale) + lower.slice(1);
}

/**
 * Long date for a heading. Danish stays sentence case ("Tirsdag den 20. oktober 2026").
 * The heading also uses Tailwind `normal-case`, because CSS `capitalize` title-cases
 * "den" and the month even when the text itself is already correct.
 */
export function formatAppointmentDateHeading(date: Date, locale: string): string {
  const formatted = date.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  if (locale.toLowerCase().startsWith('da')) {
    return danishSentenceCaseDate(formatted, locale);
  }
  return formatted.charAt(0).toLocaleUpperCase(locale) + formatted.slice(1);
}

function dayOfMonth(date: Date, timeZone?: string): number {
  if (!timeZone) return date.getDate();
  return zonedDateTime(date, timeZone).day;
}

export function formatWeekPeriod(
  start: Date,
  end: Date,
  language: string,
  timeZone?: string,
): string {
  const locale = appointmentDateLocale(language);
  const startDay = dayOfMonth(start, timeZone);
  const endLabel = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).format(end);
  if (language.startsWith('da')) return `${startDay}.–${endLabel}`;
  return `${startDay}–${endLabel}`;
}

/**
 * Heading under Aftalebog. Liste shows the month it loads (same arrows as
 * Måned), so it uses the month name. A week number is only paired with that
 * week's own dates — never with a whole month.
 */
export function formatAppointmentRangeLabel(options: {
  view: AppointmentRangeView;
  anchor: Date;
  language: string;
  weekTitle: (week: number) => string;
  timeZone?: string;
}): string {
  const { view, anchor, language, weekTitle, timeZone } = options;
  switch (view) {
    case 'day':
      return new Intl.DateTimeFormat(appointmentDateLocale(language), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone,
      }).format(anchor);
    case 'month':
    case 'agenda':
      return new Intl.DateTimeFormat(appointmentDateLocale(language), {
        month: 'long',
        year: 'numeric',
        timeZone,
      }).format(anchor);
    case 'week': {
      const start = timeZone ? startOfZonedWeek(anchor, timeZone) : startOfWeekMonday(anchor);
      const end = timeZone ? addZonedDays(start, 6, timeZone) : addDays(start, 6);
      const week = timeZone
        ? isoWeekNumberFromParts(zonedDateTime(start, timeZone))
        : isoWeekNumber(start);
      return `${weekTitle(week)} · ${formatWeekPeriod(start, end, language, timeZone)}`;
    }
    default: {
      const _exhaustive: never = view;
      return _exhaustive;
    }
  }
}

/**
 * Labels wide enough to reserve the heading slot. Dag, Uge, Måned and Liste
 * all fit, including uge 53 → uge 1, so the toolbar does not shift when the
 * visible text gets shorter.
 */
export function appointmentPeriodLabelWidthSamples(
  language: string,
  weekTitle: (week: number) => string,
): string[] {
  const samples = new Set<string>();
  const add = (view: AppointmentRangeView, anchor: Date) => {
    samples.add(formatAppointmentRangeLabel({ view, anchor, language, weekTitle }));
  };
  for (const year of [2026, 2027]) {
    for (let month = 0; month < 12; month += 1) {
      const lastDay = new Date(year, month + 1, 0).getDate();
      add('day', new Date(year, month, lastDay));
      add('month', new Date(year, month, 1));
      add('agenda', new Date(year, month, 1));
      for (let day = 1; day <= lastDay; day += 7) {
        add('week', new Date(year, month, day));
      }
    }
  }
  return [...samples];
}

export function isoWeekNumber(date: Date): number {
  return isoWeekNumberFromParts({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });
}

function isoWeekNumberFromParts(parts: { year: number; month: number; day: number }): number {
  const utc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil(((utc.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}
